import { useSyncExternalStore } from "react";

/**
 * Which business the member is currently looking at.
 *
 * Most members belong to one business and will never see this. The member with a participant at
 * the swimming academy and another at the pilates studio is the case it exists for: the Book tab,
 * the packages list and the credit balance all have to be about one business at a time, and the
 * member should not have to re-pick it on every screen or after every reload.
 *
 * Deliberately the same shape as `auth/auth-store.ts`: one string, three operations, localStorage
 * for durability and `useSyncExternalStore` to read it from React without tearing. A state library
 * for this would be more code than the thing it stores. The web twin of the `activeBusinessId`
 * slice of the mobile app's zustand store.
 *
 * A stored id is only ever a hint. `useActiveBusiness` falls back to the first business whenever
 * the stored one is not in the list any more, so a member who left a business never lands on an
 * empty screen because of a stale value.
 */

const STORAGE_KEY = "teamna-web-active-business";

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Safari in private mode throws on any localStorage access, and a session-long choice is
    // better than a broken screen.
    return null;
  }
}

let activeBusinessId: string | null = typeof window === "undefined" ? null : readStored();
const listeners = new Set<() => void>();

export function getActiveBusinessId(): string | null {
  return activeBusinessId;
}

export function setActiveBusinessId(next: string | null): void {
  if (activeBusinessId === next) return;
  activeBusinessId = next;
  try {
    if (next === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Kept in memory only. See readStored.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The stored choice, as a React value. */
export function useActiveBusinessId(): string | null {
  return useSyncExternalStore(subscribe, getActiveBusinessId, getActiveBusinessId);
}
